import React, { useEffect, useMemo, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { PlaceValueBlocks } from '../../../visuals/components/PlaceValueBlocks';
import { Plus, Minus } from 'lucide-react';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import type { PlaceValueBuildConfig } from '../../../content/types';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
function PlaceValueControl({
  value,
  setValue,
  label
}: {
  value: number;
  setValue: (val: number | ((prev: number) => number)) => void;
  label: string;
}) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);
  const handlePressStart = (increment: number) => {
    hapticManager.hapticLight();
    setValue(prev => Math.max(0, Math.min(9, prev + increment)));
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        hapticManager.hapticLight();
        setValue(prev => Math.max(0, Math.min(9, prev + increment)));
      }, 100);
    }, 500);
  };
  const handlePressEnd = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };
  return <div className="flex flex-col items-center mt-4" data-id="element-10894">
      <span className="text-xs font-bold text-slate-500 mb-2" data-id="element-10895">{label}</span>
      <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl border border-slate-200" data-id="element-10896">
        <button onPointerDown={() => handlePressStart(-1)} onPointerUp={handlePressEnd} onPointerLeave={handlePressEnd} disabled={value <= 0} className="w-11 h-11 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors select-none touch-manipulation" aria-label="Verwijder" data-id="element-10897">
          <Minus className="w-5 h-5" data-id="element-10898" />
        </button>
        <span className="w-6 text-center text-xl font-bold text-slate-800" data-id="element-10899">
          {value}
        </span>
        <button onPointerDown={() => handlePressStart(1)} onPointerUp={handlePressEnd} onPointerLeave={handlePressEnd} disabled={value >= 9} className="w-11 h-11 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors select-none touch-manipulation" aria-label="Voeg toe" data-id="element-10900">
          <Plus className="w-5 h-5" data-id="element-10901" />
        </button>
      </div>
    </div>;
}
/**
 * PlaceValueBuildRenderer — Compose a number by adding/removing MAB blocks.
 *
 * Sprint R1 fixes:
 * - F1: honour moduleColor for the running-total banner + active controls
 * - F5: read config from typed data.placeValueBuild namespace (with legacy
 *   fallback to data root for backwards compat during seed migration)
 */
export function PlaceValueBuildRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  // Prefer namespaced config; fall back to legacy data-root shape during
  // migration period. Both are typed via PlaceValueBuildConfig.
  const namespaced = question.data?.placeValueBuild;
  const legacy = (question.data || {}) as Partial<PlaceValueBuildConfig> & {
    target?: number;
    initial?: PlaceValueBuildConfig['initial'];
  };
  const config: Partial<PlaceValueBuildConfig> = namespaced ?? legacy;
  const initial = config.initial || {
    thousands: 0,
    hundreds: 0,
    tens: 0,
    ones: 0
  };
  const [thousands, setThousands] = useState(initial.thousands || 0);
  const [hundreds, setHundreds] = useState(initial.hundreds || 0);
  const [tens, setTens] = useState(initial.tens || 0);
  const [ones, setOnes] = useState(initial.ones || 0);
  const isReady = thousands > 0 || hundreds > 0 || tens > 0 || ones > 0;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  const answerPayload = useMemo(() => thousands * 1000 + hundreds * 100 + tens * 10 + ones, [thousands, hundreds, tens, ones]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  useEffect(() => {
    setThousands(initial.thousands || 0);
    setHundreds(initial.hundreds || 0);
    setTens(initial.tens || 0);
    setOnes(initial.ones || 0);
    // initial is read from config; resetting on question change is intended
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);
  const composedNumber = thousands * 1000 + hundreds * 100 + tens * 10 + ones;
  return <div className="w-full max-w-3xl mx-auto space-y-8" data-id="element-10902">
      <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-id="element-10903">
        <PlaceValueBlocks thousands={thousands} hundreds={hundreds} tens={tens} ones={ones} showLabels={false} showTotal={false} size="md" data-id="element-10904" />

        <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mt-4 pt-6 border-t border-slate-100" data-id="element-10905">
          <PlaceValueControl value={thousands} setValue={setThousands} label="Duizendtal" data-id="element-10906" />
          <PlaceValueControl value={hundreds} setValue={setHundreds} label="Honderdtal" data-id="element-10907" />
          <PlaceValueControl value={tens} setValue={setTens} label="Tiental" data-id="element-10908" />
          <PlaceValueControl value={ones} setValue={setOnes} label="Eenheid" data-id="element-10909" />
        </div>
      </div>

      <div className="rounded-xl p-6 text-center border-2" style={{
      backgroundColor: hexToRgba(color, 0.08),
      borderColor: hexToRgba(color, 0.25)
    }} data-id="element-10910">
        <span className="text-sm font-bold uppercase tracking-wider mb-2 block" style={{
        color
      }} data-id="element-10911">
          Jouw getal
        </span>
        <span className="text-4xl font-mono font-black" style={{
        color: hexToRgba(color, 0.95)
      }} data-id="element-10912">
          {composedNumber.toLocaleString('nl-NL')}
        </span>
      </div>
    </div>;
}